/**
 * Distance math. All of it runs in the browser — no server round-trip.
 */

const EARTH_RADIUS_MILES = 3958.7613;
const EARTH_RADIUS_KM = 6371.0088;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

/**
 * Haversine great-circle distance between two lat/long pairs.
 * Plain JS, no dependencies.
 *
 *   a = sin²(Δφ/2) + cos φ₁ · cos φ₂ · sin²(Δλ/2)
 *   d = 2R · atan2(√a, √(1−a))
 */
export function haversineDistance(from, to, unit = 'mi') {
  if (!from || !to) return null;
  if (!Number.isFinite(from.lat) || !Number.isFinite(from.long)) return null;
  if (!Number.isFinite(to.lat) || !Number.isFinite(to.long)) return null;

  const radius = unit === 'km' ? EARTH_RADIUS_KM : EARTH_RADIUS_MILES;

  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLong = toRadians(to.long - from.long);

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLong / 2) ** 2;

  return 2 * radius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Attach `distance_mi` to every record, relative to the user. Null when either side lacks coords. */
export function withDistances(hackathons, userLocation) {
  if (!userLocation) return hackathons.map((h) => ({ ...h, distance_mi: null }));

  return hackathons.map((h) => ({
    ...h,
    distance_mi: haversineDistance(
      { lat: userLocation.lat, long: userLocation.long },
      { lat: h.lat, long: h.long },
    ),
  }));
}

export function formatDistance(miles) {
  if (miles == null) return null;
  if (miles < 1) return '< 1 mi';
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles).toLocaleString()} mi`;
}

/* -------------------------------------------------------------------------- */
/* Resolving where the user is                                                 */
/* -------------------------------------------------------------------------- */

/** Browser geolocation, promisified. Rejects if the user declines or it's unavailable. */
export function requestBrowserLocation({ timeout = 10000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Geolocation is not available in this browser'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          long: position.coords.longitude,
          source: 'browser',
        }),
      (error) => reject(new Error(error.message || 'Location permission denied')),
      { timeout, maximumAge: 15 * 60 * 1000, enableHighAccuracy: false },
    );
  });
}

/**
 * Reverse-geocode coordinates to a city/state label.
 *
 * This exists for two reasons: to show the user where we think they are, and to make
 * the "Statewide" radius preset meaningful — that preset filters on state name, which
 * browser geolocation alone can't give us. Free, keyless, CORS-open.
 * If it fails, we keep the coordinates and just lose the state label.
 */
export async function reverseGeocode({ lat, long }) {
  const url = new URL('https://api.bigdatacloud.net/data/reverse-geocode-client');
  url.searchParams.set('latitude', String(lat));
  url.searchParams.set('longitude', String(long));
  url.searchParams.set('localityLanguage', 'en');

  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Reverse geocode failed (HTTP ${res.status})`);

  const body = await res.json();
  return {
    city: body.city || body.locality || null,
    state: body.principalSubdivision || null,
    country: body.countryCode || null,
  };
}

/**
 * Forward-geocode a typed city name — the manual fallback when the user declines
 * the geolocation prompt. Free, keyless, CORS-open (GeoNames data via Open-Meteo).
 */
export async function searchCities(query, { limit = 5 } = {}) {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const url = new URL('https://geocoding-api.open-meteo.com/v1/search');
  url.searchParams.set('name', trimmed);
  url.searchParams.set('count', String(limit));
  url.searchParams.set('language', 'en');
  url.searchParams.set('format', 'json');

  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`City search failed (HTTP ${res.status})`);

  const body = await res.json();
  return (body.results || []).map((r) => ({
    id: r.id,
    lat: r.latitude,
    long: r.longitude,
    city: r.name,
    state: r.admin1 || null,
    country: r.country_code || null,
    label: [r.name, r.admin1, r.country_code].filter(Boolean).join(', '),
    source: 'search',
  }));
}
